import { createSignal, createEffect, Setter, For, createMemo, createContext, useContext } from 'solid-js'
import STButton, { ButtonType } from '../st-button/st-button';
import STInput from '../st-input/st-input';
import STRange from '../st-range-slider/st-range-slider';
import STToolButton from '../st-tool-button/st-tool-button';
import STAlert, { AlertType } from '../st-alert/st-alert';
import { MyWritingStyleModal } from './MyWritingStyleModal';
import { gridGeneratorBtn as api } from 'st-app/api'
import { Verbosity, WritingStylePrompt } from 'st-app/api';
import { GridStore } from './GridStore';
import STModal from '../st-modal/st-modal';
import './st-grid-generator-btn.less';
import styles from './st-grid-generator-btn.module.css';
import { GeneratedTextPreview, SelectedText } from './st-generated-text-preview';
import { toast } from 'common';

export const GridStoreContext = createContext<GridStore>(new GridStore());
export function useGridStore() { return useContext<GridStore>(GridStoreContext); }

const ST = (window as any).ST || {};

class STGeneratorEvents {
    onOpen?: () => void;
    onClose?: () => void;
    onReplace?: (text: string[]) => void;
}

export class LookupItem {
    code: string = '';
    desc: string = '';

    generatedText = createSignal<string[]>([]);
    generatedItems = createSignal<SelectedText[]>([]);
    loading = createSignal(false);
}

class GridGeneratorBtnOptions {
    srcName: string = '';
    identity: string = '';
    boid: string = '';
    lookupItems: LookupItem[] = [new LookupItem()];
    onSelected?: (val: string) => void;
    events: STGeneratorEvents = new STGeneratorEvents();
}

export default function GridGeneratorBtn(props: GridGeneratorBtnOptions) {
    const [showModal, setShowModal] = createSignal(!!ST.DEBUGGING_COMPONENT);
    const [lookupItems, setLookupItems] = createSignal(props.lookupItems);

    const gridStore = new GridStore();
    const showWritingConfigModal = createSignal(false);
    const selectedWritingStyle = createSignal(gridStore.myWritingStyle().writingStyleGU);

    const [hintText, setHintText] = createSignal('');
    const identity = createMemo(() => props.identity);
    const srcName = createMemo(() => props.srcName);
    const boid = createMemo(() => props.boid);

    // maintain a local version of verbosity
    const [currVerbosity, setCurrVerbosity] = createSignal(gridStore.myWritingStyle().verbosity);

    createEffect(() => {
        const isOpen = showModal();

        if (isOpen && props.events.onOpen) {
            props.events.onOpen();
        }
        else if (!isOpen && props.events.onClose) {
            props.events.onClose();
        }

        // Reset all text fields
        setHintText('');
        for (const item of lookupItems()) {
            if (!isOpen) {
                item.generatedText[1]([]);
            }
        }
    });

    // When we update myWritingStyle, also update the verbosity slider
    createEffect(() => {
        const writingStyle = gridStore.myWritingStyle();

        // ensure we only trigger this when myWritingStyle changes
        if (writingStyle) {
            requestAnimationFrame(() => {
                if (writingStyle.verbosity !== currVerbosity()) {
                    setCurrVerbosity(writingStyle.verbosity);
                }
            });
        }
    });

    // Flip it, and when we update the verbosity slider, we also update myWritingStyle
    createEffect(() => {
        const verbosity = currVerbosity();

        // ensure we only trigger this when myWritingStyle changes
        requestAnimationFrame(() => {
            const writingStyle = gridStore.myWritingStyle();
            if (writingStyle.verbosity !== verbosity) {
                writingStyle.verbosity = verbosity;
                gridStore.setMyWritingStyle(writingStyle);
            }
        });
    });

    // Check the UI object for the list of items to use
    createEffect(() => {
        const boid = props.boid;

        if (boid) {
            api.GetMemoLookupItems(boid)
                .then(res => {
                    const items = res.map(it => {
                        const item = new LookupItem();
                        item.code = it.code || '';
                        item.desc = it.desc || '';

                        return item;
                    }) || [];

                    if (items.length === 0) {
                        const item = new LookupItem();
                        items.push(item);
                    }

                    setLookupItems(items);
                });
        }
    });


    return <GridStoreContext.Provider value={gridStore}>
        <STToolButton icon="fas fa-brain-circuit s-link" label="Generate Rows" onClick={() => setShowModal(true)}></STToolButton>

        <STModal class={`${styles['st-grid-gen']} st-grid-gen`} show={[showModal, setShowModal]}>
            <STAlert type="info">What would you like to say?</STAlert>
            <div class="flexbox horizontal middle">
                <span class="auto" style="margin: 0 5px">
                    <STInput value={[hintText, setHintText]} onEnterKey={generateResponses} />
                </span>
                <STButton type="primary" onClick={generateResponses}>
                    Compose
                </STButton>
            </div>

            <div class="flexbox vertical middle">
                <div style="max-width: 600px; width: 100%">
                    <STRange type={Verbosity} value={[currVerbosity, setCurrVerbosity]} label="Verbosity" />
                </div>
            </div>

            <div class="auto flexbox horizontal">
                <For each={lookupItems()}>{(it) =>
                    <GeneratedTextPreview label={it.desc} onReplace={() => onReplace(it.generatedItems[0]())} loading={it.loading[0]()} generatedText={it.generatedText[0]()} generatedItems={it.generatedItems[0]()} />
                }</For>
            </div>

            <div class="flexbox horizontal middle padded">
                <div>
                    <STButton onClick={() => showWritingConfigModal[1](true)} tooltip="Configure My Writing Style">
                        <i class="fas fa-pen-nib"></i>
                    </STButton>
                </div>
                <div class="auto"></div>
                <div>
                    <STButton onClick={() => setShowModal(false)}>Close</STButton>
                </div>
            </div>

            <MyWritingStyleModal show={showWritingConfigModal}></MyWritingStyleModal>
        </STModal>
    </GridStoreContext.Provider>;

    function onReplace(textArr: SelectedText[]) {
        if (props.events.onReplace) {
            var res = [];
            for (var item of textArr) {
                if (item.selected()) {
                    res.push(item.text)
                }
            }
            props.events.onReplace(res);
        }

        for (const item of lookupItems()) {
            item.generatedText[1]([]);
        }

        setShowModal(false);
    }

    async function generateResponses() {
        const style = gridStore.getWritingStyle(selectedWritingStyle[0]());

        for (const item of lookupItems()) {
            item.loading[1](true);
            item.generatedText[1]([]);
            item.generatedItems[1]([]);

            api.Generate(hintText(), identity(), srcName(), boid(), item.code, style)
                .then((res) => new Promise<any>(r => setTimeout(() => r(res), 1000)))
                .then(res => {
                    item.generatedText[1](res.text);
                    for (var textLine of res.text) {
                        item.generatedItems[1]((prev) => [...prev, new SelectedText(textLine)]);
                    }
                    item.loading[1](false);
                })
                .catch(err => {
                    toast.notify('error', err);
                    item.loading[1](false);
                });
        }
    }

}

GridGeneratorBtn.defaultProps = new GridGeneratorBtnOptions();